import { firestoreService } from './firestoreService';

// ─────────────────────────────────────────────────────────────────────────
// A DÓNDE LLEVA UN AVISO QUE NO ES DEL VIAJE — V18/V37, 16-sep-2026.
// ─────────────────────────────────────────────────────────────────────────
// Lo usan el toque del push (app/_layout.tsx) y la campana
// (app/notifications.tsx), para que los dos manden al mismo sitio.
//
// Los avisos de comida no dicen a quién van: ORDER_ASSIGNED le llega al
// cliente y al restaurante, ORDER_CANCELLED al cliente, al repartidor y al
// restaurante (functions/src/dispatch.ts, foodDispatch.ts, cancelOrder.ts).
// Antes todos abrían /order-tracking, la pantalla del CLIENTE. Aquí se decide
// por el lugar que ocupa quien toca dentro del pedido y, si el pedido no se
// puede leer, por su rol.
// ─────────────────────────────────────────────────────────────────────────

export type DestinoDeAviso = { pathname: string; params?: Record<string, string> };

// Avisos que no abren nada en la app: el SOS y los casos de fraude los atiende
// el administrador desde el panel web.
export const AVISOS_SIN_NAVEGACION = new Set(['SOS_ALERT', 'AVISO_FRAUDE_REVISAR']);

const AVISOS_DE_COMIDA = new Set([
  'NEW_FOOD_ORDER',
  'FOOD_ORDER_DISPATCH',
  'FOOD_ORDER_PICKUP_REMINDER',
  'ORDER_PREPARING',
  'ORDER_READY',
  'ORDER_ASSIGNED',
  'ORDER_PICKED_UP',
  'ORDER_DELIVERED',
  'ORDER_CANCELLED',
  'ORDER_MESSAGE',
  'ORDER_SIN_REPARTIDOR',
]);

// Solo le llegan al cliente.
const SOLO_CLIENTE = new Set(['ORDER_PREPARING', 'ORDER_PICKED_UP', 'ORDER_DELIVERED']);

export function esAvisoDeComida(tipo: string): boolean {
  return AVISOS_DE_COMIDA.has(tipo);
}

/** Lee el pedido una vez, con plazo. `null` si no existe, no se puede leer o no llega. */
function leerPedidoUnaVez(orderId: string, ms: number): Promise<any | null> {
  return new Promise((resolve) => {
    let hecho = false;
    let quitar: (() => void) | null = null;
    const terminar = (valor: any | null) => {
      if (hecho) return;
      hecho = true;
      clearTimeout(reloj);
      try { quitar?.(); } catch {}
      resolve(valor);
    };
    const reloj = setTimeout(() => terminar(null), ms);
    try {
      const unsub: any = firestoreService.listenToFoodOrderById(orderId, (pedido) => terminar(pedido));
      quitar = typeof unsub === 'function' ? unsub : null;
      // Si el primer dato llegó antes de tener con qué desuscribir.
      if (hecho) { try { quitar?.(); } catch {} }
    } catch {
      terminar(null);
    }
  });
}

export async function destinoDeAvisoDeComida(
  tipo: string,
  orderId: string | null,
  uid: string | null,
  rol: string | null
): Promise<DestinoDeAviso | null> {
  if (!AVISOS_DE_COMIDA.has(tipo)) return null;

  if (tipo === 'NEW_FOOD_ORDER' || tipo === 'ORDER_READY') return { pathname: '/restaurant-dashboard' };
  if (tipo === 'FOOD_ORDER_DISPATCH') return { pathname: '/deliveries-feed' };

  if (!orderId) {
    if (rol === 'RESTAURANT_OWNER') return { pathname: '/restaurant-dashboard' };
    if (rol === 'DELIVERY_DRIVER') return { pathname: '/deliveries-feed' };
    return { pathname: '/(tabs)/activity' };
  }

  if (tipo === 'FOOD_ORDER_PICKUP_REMINDER') return { pathname: '/delivery-active', params: { orderId } };
  if (SOLO_CLIENTE.has(tipo)) return { pathname: '/order-tracking', params: { orderId } };

  // ORDER_ASSIGNED, ORDER_CANCELLED, ORDER_MESSAGE y ORDER_SIN_REPARTIDOR van a
  // más de un lado del pedido: se mira cuál es el de quien toca.
  const pedido = uid ? await leerPedidoUnaVez(orderId, 4000) : null;
  let lado: 'CLIENTE' | 'REPARTIDOR' | 'RESTAURANTE' | null = null;
  if (pedido && uid) {
    if (pedido.customerId === uid) lado = 'CLIENTE';
    else if (pedido.driverId === uid) lado = 'REPARTIDOR';
    else if (pedido.restaurantOwnerId === uid) lado = 'RESTAURANTE';
  }
  if (!lado) {
    lado = rol === 'RESTAURANT_OWNER' ? 'RESTAURANTE' : rol === 'DELIVERY_DRIVER' ? 'REPARTIDOR' : 'CLIENTE';
  }

  if (lado === 'RESTAURANTE') return { pathname: '/restaurant-dashboard' };
  if (tipo === 'ORDER_MESSAGE') return { pathname: '/order-chat', params: { orderId } };
  if (lado === 'REPARTIDOR') {
    const estado = String(pedido?.status ?? '').toUpperCase();
    if (tipo === 'ORDER_CANCELLED' || estado === 'CANCELLED' || estado === 'DELIVERED') {
      return { pathname: '/deliveries-feed' };
    }
    return { pathname: '/delivery-active', params: { orderId } };
  }
  return { pathname: '/order-tracking', params: { orderId } };
}
